import { type ComponentProps } from "react";

import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

export type FormButtonVariant = "accent" | "outline" | "secondary" | "default" | "solid";

export function FormButton({
  type = "button",
  variant = "default",
  size,
  className,
  disabled,
  isBusy,
  children,
  ...rest
}: ComponentProps<"button"> & {
  variant?: FormButtonVariant;
  size?: "sm" | "default";
  isBusy?: boolean;
}) {
  const enabled = !(disabled || isBusy);
  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={type}
      aria-busy={isBusy}
      disabled={!enabled}
      className={twMerge(
        "relative rounded-xl px-4 h-11 tracking-tight flex flex-row items-center justify-center",
        enabled && "active:translate-y-[1px]",

        disabled && "opacity-60",
        size === "sm" && "rounded-8 h-10 text-sm text-blue-500 bg-blue-50 dark:bg-blue-950",

        variant === "default" && "shadow-block",
        variant === "default" && enabled && "hover:shadow-block-selected hover:outline-none",

        variant === "accent" && "bg-blue-100 dark:bg-blue-900  text-blue-500 dark:text-blue-200",
        variant === "accent" &&
          enabled &&
          "hover:bg-blue-200 dark:hover:bg-blue-800 focus:bg-blue-200 dark:focus:bg-blue-800",

        variant === "secondary" && "shadow-block bg-transparent text-blue-500",
        variant === "secondary" && enabled && "hover:shadow-block-selected hover:outline-none",

        variant === "outline" &&
          "border border-blue-500 bg-transparent dark:bg-transparent hover:bg-blue-50 dark:hover:bg-gray-900 focus:bg-transparent dark:focus:bg-transparent",

        variant === "solid" &&
          "bg-white text-black dark:bg-gray-800 dark:text-white hover:opacity-60",

        className,
      )}
      {...rest}
    >
      {isBusy ? (
        <>
          <div className="absolute top-0 left-0 w-full h-full flex">
            <Icon name="Spinner" className="m-auto animate-spin inline-block" />
          </div>
          <div className="flex flex-row items-center justify-center opacity-0">{children}</div>
        </>
      ) : (
        children
      )}
    </button>
  );
}
